import { Button, Center, Flex, Spinner, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plural, Trans } from '@lingui/react/macro';
import { Sparkles } from 'lucide-react';
import pickBy from 'lodash-es/pickBy.js';

import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { emptyPaginationResponse } from '@duke-hq/lib/pagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { UnitGenerationRunStatus } from '@duke-hq/svc-api-db/client';

import { withPermission } from '../../withPermission';
import { RunFilters } from './components/RunFilters';
import { RunCard } from './components/RunCard';
import { CreateRunModal } from './components/CreateRunModal';
import { RunDetailDrawer } from './components/RunDetailDrawer';
import { AllocationReviewModal } from './components/AllocationReviewModal';
import { useGenerationProgressSubscription } from './useGenerationProgressSubscription';

const RunProgressSubscriber = ({
  runId,
  status,
  error,
}: {
  runId: string;
  status: UnitGenerationRunStatus;
  error: string | null;
}) => {
  useGenerationProgressSubscription(runId, status, error);
  return null;
};

type URLFilters = {
  status?: UnitGenerationRunStatus;
  unitCode?: string;
  page?: string;
};

export const UnitGenerationPage = withPermission('canAccessAIContentGeneration', () => {
  const { currentPage, pageSize, setPage } = usePagination();
  const createModal = useDisclosure();
  const createModalKey = useRef(0);

  const [searchParams, setSearchParams] = useSearchParams({ page: '1' });
  const selectedRunId = searchParams.get('run');
  const reviewRunId = searchParams.get('review');

  const getURLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { status } = urlFilters;
    const validStatus = Object.values(UnitGenerationRunStatus).find((s) => s === status);
    return { status: validStatus, unitCode: urlFilters.unitCode };
  }, [searchParams]);

  const { data: runs, isFetching } = trpcAdmin.unitsOfCompetency.listGenerations.useQuery(
    {
      filters: {
        status: getURLFilters.status,
        unitCode: getURLFilters.unitCode,
      },
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const handleFilterChange = useCallback(
    (newFilters: Partial<URLFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value !== '',
      );

      if (updatedFilters.page != null && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setRunParam = useCallback(
    (key: 'run' | 'review', runId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          if (runId == null) {
            next.delete(key);
          } else {
            next.set(key, runId);
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage],
  );

  return (
    <>
      <TableViewLayout
        pageHeader={[
          <PageHeading key="unit-generation">
            <Trans>AI Unit Generation</Trans>
          </PageHeading>,
        ]}
        pageActions={[
          <Button
            key="create"
            colorScheme="brand"
            size="sm"
            leftIcon={<Sparkles size={16} />}
            onClick={() => {
              createModalKey.current++;
              createModal.onOpen();
            }}
          >
            <Trans>Generate Unit</Trans>
          </Button>,
        ]}
        filters={
          <Flex gap={2} align="center">
            <SearchInput
              w="220px"
              urlKey="unitCode"
              onCommit={(v) => handleFilterChange({ unitCode: v || undefined })}
            />
            <RunFilters filters={getURLFilters} onChange={handleFilterChange} />
          </Flex>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={runs.pagination.totalCount}
              zero="0 units"
              one="1 unit"
              other="# units"
            />
          </Text>
        }
        table={
          <>
            {runs.items.length > 0 ? (
              <VStack align="stretch" spacing={3} p={4}>
                {runs.items.map((run) => (
                  <RunCard
                    key={run.id}
                    run={run}
                    onViewDetails={() => setRunParam('run', run.id)}
                    onReviewAllocation={() => setRunParam('review', run.id)}
                  />
                ))}
              </VStack>
            ) : null}

            {isFetching ? (
              <Center p={8}>
                <Spinner size="xl" />
              </Center>
            ) : runs.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No generation runs found</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          runs.items.length > 0 ? (
            <Pagination pagination={runs.pagination} onPageChange={handlePageChange} />
          ) : null
        }
      />

      {runs.items
        .filter(
          (run) =>
            run.status === UnitGenerationRunStatus.RUNNING ||
            (run.status === UnitGenerationRunStatus.GENERATED && run.error == null),
        )
        .map((run) => (
          <RunProgressSubscriber
            key={run.id}
            runId={run.id}
            status={run.status}
            error={run.error}
          />
        ))}

      <CreateRunModal
        key={createModalKey.current}
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
      />

      {selectedRunId != null ? (
        <RunDetailDrawer
          runId={selectedRunId}
          isOpen
          onClose={() => setRunParam('run', null)}
          onReviewAllocation={() => setRunParam('review', selectedRunId)}
        />
      ) : null}

      {reviewRunId != null ? (
        <AllocationReviewModal
          key={reviewRunId}
          runId={reviewRunId}
          isOpen
          onClose={() => setRunParam('review', null)}
        />
      ) : null}
    </>
  );
});
